import { useCallback, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { RootShareRecommendations } from 'src/app/pages/starterDashboardV2/shared/hooks';

import { useFeedbackModal } from '../useFeedbackModal';

export type Recommendation = NonNullable<
	NonNullable<RootShareRecommendations['facebook'] | RootShareRecommendations['reddit']>[0]
>;
type Vote = 'like' | 'dislike';
export type PlatformType = 'facebook' | 'reddit';
type UseShareRecommendationsReturn = ModelHookResult<
	undefined,
	{
		handleArrowButtonClick: (arrow: 'left' | 'right', context: string) => void;
		handleChipClick: (platform: 'all' | 'reddit' | 'facebook', context: string) => void;
		handleLinkClick: (
			recommendation: string,
			location: string,
			source: string | null | undefined,
			viewContext: string,
		) => void;
		handleVoteUp: (recommendation: Recommendation) => void;
		handleVoteDown: (recommendation: Recommendation, type: PlatformType) => Promise<void>;
		isVoted: (vote: Vote) => boolean;
	}
>;

export const SHARE_RECOMMENDATION_LINK_CLICK_CONTEXT_EVENT = 'petition_dashboard_group_rec';
export const SHARE_RECOMMENDATION_LINK_CLICK_EVENT = 'share_petition';
export const SHARE_RECOMMENDATION_GROUP_CARD_CLICK_EVENT = 'petition_manage_recommended_groups_click';
export const SHARE_RECOMMENDATIONS_FEEDBACK_MODAL_EVENT = 'petition_manage_recommendations_feedback_click';
export const SHARE_RECOMMENDATIONS_CHIP_CLICK_EVENT = 'petition_manage_recommendations_toggle_click';
export const SHARE_RECOMMENDATIONS_SCROLL_ARROW_CLICK_EVENT = 'petition_manage_recommendations_arrow_click';

// TODO: Refactor this file to avoid having a large function
// eslint-disable-next-line max-lines-per-function
export function useShareRecommendationsTracking(): UseShareRecommendationsReturn {
	const [voted, setVoted] = useState<Vote>();
	const {
		data: {
			petition: { id },
		},
	} = useStarterDashboardV2PageData();
	const track = useTracking();
	const openFeedbackModal = useFeedbackModal();
	const { translate } = useI18n();

	const isVoted = useCallback(
		(vote: Vote) => {
			return voted === vote;
		},
		[voted],
	);

	const handleVoteUp = useCallback(
		(recommendation: Recommendation) => {
			setVoted((prevVoted) => {
				if (prevVoted !== 'like') {
					void track(SHARE_RECOMMENDATIONS_FEEDBACK_MODAL_EVENT, {
						recommendation_rating: 'like',
						recommendation_group_name: recommendation.name,
						recommendation_group_url: recommendation.url,
					});
				}
				return 'like';
			});
		},
		[track, setVoted],
	);

	const handleVoteDown = useCallback(
		async (recommendation: Recommendation, type: PlatformType) => {
			let prevValue: Vote | undefined;
			setVoted((prevVoted) => {
				prevValue = prevVoted;
				return 'dislike';
			});

			if (prevValue !== 'dislike') {
				const result = await openFeedbackModal({
					recommendationGroup: translate(
						`corgi.petition.starter_dashboard.recommendations.feedback_modal.platform.${type}`,
					),
					recommendation,
				}).waitForResult();
				if (!result?.submitted) {
					void track(SHARE_RECOMMENDATIONS_FEEDBACK_MODAL_EVENT, {
						petition_id: id,
						recommendation_rating: 'dislike',
						recommendation_group_name: recommendation.name,
						recommendation_group_url: recommendation.url,
						recommendation_group_feedback: '[]',
					});
				}
			}
		},
		[setVoted, openFeedbackModal, translate, track, id],
	);

	const handleLinkClick = useCallback(
		(recommendation: string, location: string, source: string | null | undefined, viewContext: string) => {
			void track(SHARE_RECOMMENDATION_GROUP_CARD_CLICK_EVENT, {
				context: SHARE_RECOMMENDATION_LINK_CLICK_CONTEXT_EVENT,
				location,
				recommendation,
				recommendation_source: source,
				tab_at_share: viewContext,
			});
		},
		[track],
	);

	const handleChipClick = useCallback(
		(platform: 'all' | 'reddit' | 'facebook', context: string) => {
			void track(SHARE_RECOMMENDATIONS_CHIP_CLICK_EVENT, {
				toggle: platform,
				context,
			});
		},
		[track],
	);

	const handleArrowButtonClick = useCallback(
		(arrow: 'left' | 'right', context: string) => {
			void track(SHARE_RECOMMENDATIONS_SCROLL_ARROW_CLICK_EVENT, {
				arrow,
				context,
			});
		},
		[track],
	);

	return {
		actions: {
			handleArrowButtonClick,
			handleChipClick,
			handleLinkClick,
			handleVoteUp,
			handleVoteDown,
			isVoted,
		},
	};
}
